import { Button, GeneralSelect, StyledText } from '@clickcease/cc-client-components-lib'
import React, { useEffect, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { OutsideLink } from '../../../../../../../components/Elements/Link/OutsideLink.styled'
import { WrapperWithButtons } from '../../../../../../../components/Elements/WrapperWithButtons/WrapperWithButtons'
import { useGetPlatformConnections } from '../../../../../api/getPlatformConnections'
import { useGetMetaProtectionMode } from '../../../../CampaignsModal/api/getFacebookProtectionMode'
import { useSetCampaignsProtectionMode } from '../../../../CampaignsModal/api/setCampaignsProtectionMode'
import {
  AnnotationContainer,
  OptionContainer,
  RadioGroupIndicator,
  RadioGroupItem,
  RadioGroupRoot,
} from '../../../../CampaignsModal/ModalContent/SelectProtectionType/SelectProtectionType.styled'
import { useSelectAdsRequests } from '../SelectAdSets/useSelectAdsRequests'
import { PLATFORMS, PROTECTION_TYPES, ProtectionType } from '../../../../../models'
import { FB_LINKS } from '../../../../../../../utils/facebookLinks'
import { PLATFORMS_NAMES } from '../../../../../../../utils/platforms'

import { ValueLabel } from '../../../../../../../types/SelectOption'

import {
  CampaignSettingsTitleSectionContainer,
  SuccessMessageContainer,
  BottomContainer,
} from './CampaignSettings.styled'

export const CampaignSettings = ({
  onBack,
  isBackDisabled,
  onSubmit,
  accountId,
}: {
  onSubmit: () => void
  accountId: number
  onBack: () => void
  isBackDisabled?: boolean
}) => {
  const { t } = useTranslation('PMPlatforms')
  const { data: connectionsData } = useGetPlatformConnections({})
  const connections = useMemo<ValueLabel[]>(() => {
    const metaConnection: ValueLabel[] = []
    if (!(connectionsData instanceof Error) && connectionsData?.connections) {
      connectionsData.connections.forEach((connection) => {
        if (connection.platformType === PLATFORMS_NAMES.meta)
          metaConnection.push({ label: connection.displayId, value: `${connection.entityId}` })
      })
    }

    return metaConnection
  }, [connectionsData])

  const getAdAccountById = (id: number) => {
    const option = connections.find((campaign) => campaign.value === `${id}`)
    return Number(option ? option.value : connections[0].value)
  }

  const [selectedAdAccount, setSelectedAdAccount] = useState<number>(getAdAccountById(accountId))
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [isMessageVisible, setIsMessageVisible] = useState<boolean>(false)

  const { accountOptions, selectedAccountOption } = useSelectAdsRequests({
    setSelectedAdAccount,
    selectedAdAccount,
    accountId,
  })

  const { data: isAutoMode } = useGetMetaProtectionMode({
    connectionId: accountId,
    adAccountId: selectedAdAccount,
  })

  const [selectedType, setSelectedType] = useState<ProtectionType>(() =>
    isAutoMode ? PROTECTION_TYPES.AUTO : PROTECTION_TYPES.ADVANCED
  )

  useEffect(() => {
    if (isAutoMode !== undefined) {
      setSelectedType(isAutoMode ? PROTECTION_TYPES.AUTO : PROTECTION_TYPES.ADVANCED)
    }
  }, [isAutoMode])

  const handleSetProtectionType = (type: ProtectionType) => setSelectedType(type)

  const { mutate } = useSetCampaignsProtectionMode({
    connectionId: accountId,
    adAccountId: selectedAdAccount,
    platform: PLATFORMS.META,
  })

  const setProtectionType = (type: ProtectionType) => {
    mutate(
      { protectAll: type === PROTECTION_TYPES.AUTO },
      {
        onSuccess: () => {
          setSuccessMessage(
            t('meta-campaigns-wizard.protection-mode-saved', {
              mode: type === PROTECTION_TYPES.AUTO ? 'automatic' : 'manual',
              account: selectedAccountOption?.label || '',
            })
          )
          setIsMessageVisible(true)
          setTimeout(() => setIsMessageVisible(false), 3000)
        },
      }
    )
  }

  const handleNext = () => {
    onSubmit()
  }

  const handleSaveSelection = () => {
    setProtectionType(selectedType)
  }

  return (
    <WrapperWithButtons
      onGhostButtonClick={onBack}
      ghostButtonText={t('back')}
      primaryButtonText={t('next-button')}
      onPrimaryButtonClick={handleNext}
      isGhostButtonDisabled={isBackDisabled}
    >
      <CampaignSettingsTitleSectionContainer>
        <StyledText variant={'baseLight'}>
          {t('meta-campaigns-wizard.protection-selection-expanded')}
        </StyledText>
        <GeneralSelect
          options={accountOptions}
          value={selectedAccountOption}
          placeholder={t('meta-connection-wizard.account-selection-placeholder')}
          wrapperCSS={{ width: '240px' }}
          onChange={(item) => {
            if (item) setSelectedAdAccount(parseInt(item.value))
          }}
        />
      </CampaignSettingsTitleSectionContainer>

      <RadioGroupRoot
        onValueChange={handleSetProtectionType}
        value={selectedType}
        aria-label="Protection type selection"
      >
        <OptionContainer onClick={() => handleSetProtectionType(PROTECTION_TYPES.ADVANCED)}>
          <RadioGroupItem value={PROTECTION_TYPES.ADVANCED} id={PROTECTION_TYPES.ADVANCED}>
            <RadioGroupIndicator />
          </RadioGroupItem>
          <AnnotationContainer>
            <StyledText variant={'baseBold'}>
              {t('meta-campaigns-wizard.advanced-selection')}
            </StyledText>
            <StyledText variant={'baseLight'}>
              {t('meta-campaigns-wizard.advanced-selection-explainer')}
            </StyledText>
          </AnnotationContainer>
        </OptionContainer>
        <OptionContainer onClick={() => handleSetProtectionType(PROTECTION_TYPES.AUTO)}>
          <RadioGroupItem value={PROTECTION_TYPES.AUTO} id={PROTECTION_TYPES.AUTO}>
            <RadioGroupIndicator />
          </RadioGroupItem>
          <AnnotationContainer>
            <StyledText variant={'baseBold'}>
              {t('meta-campaigns-wizard.automatic-update')}
            </StyledText>
            <StyledText variant={'baseLight'}>
              <Trans ns="PMPlatforms" i18nKey="meta-connection-wizard.automatic-update-explainer">
                By checking this option, CHEQ Essentials will add all existing and new ad sets in
                your account to Meta protection. Please note, this might send all ads in your
                account into
                <OutsideLink
                  href={FB_LINKS.learningPhase}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Learning phase
                </OutsideLink>
                , for Meta to calibrate your ads optimization.
              </Trans>
            </StyledText>
          </AnnotationContainer>
        </OptionContainer>
      </RadioGroupRoot>
      <BottomContainer>
        {successMessage && (
          <SuccessMessageContainer hidden={!isMessageVisible}>
            {successMessage}
          </SuccessMessageContainer>
        )}
        <Button onClick={handleSaveSelection} text={t('meta-campaigns-wizard.save-selection')} />
      </BottomContainer>
    </WrapperWithButtons>
  )
}
